import React from 'react';

type JourneyButtonVariant = 'primary' | 'secondary' | 'danger';
type JourneyButtonSize = 'md' | 'sm';

export interface JourneyActionConfig {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: JourneyButtonVariant;
  size?: JourneyButtonSize;
}

export interface JourneyActionBarProps {
  primaryAction?: JourneyActionConfig;
  secondaryAction?: JourneyActionConfig;
  className?: string;
}

interface JourneyButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: JourneyButtonVariant;
  size?: JourneyButtonSize;
  loading?: boolean;
  className?: string;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

export const JourneyButton: React.FC<JourneyButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled,
  className,
  type = 'button',
  children,
  ...buttonProps
}) => {
  const isDisabled = Boolean(disabled || loading);

  return (
    <button
      type={type}
      className={cx(
        'money-btn',
        `money-btn--${variant}`,
        `money-btn--${size}`,
        loading && 'is-loading',
        className,
      )}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

export const JourneyActionBar: React.FC<JourneyActionBarProps> = ({
  primaryAction,
  secondaryAction,
  className,
}) => (
  <div className={className}>
    {secondaryAction ? (
      <JourneyButton
        variant={secondaryAction.variant || 'secondary'}
        size={secondaryAction.size || 'md'}
        onClick={secondaryAction.onClick}
        disabled={secondaryAction.disabled || secondaryAction.loading}
        loading={secondaryAction.loading}
      >
        {secondaryAction.label}
      </JourneyButton>
    ) : null}
    {primaryAction ? (
      <JourneyButton
        variant={primaryAction.variant || 'primary'}
        size={primaryAction.size || 'md'}
        onClick={primaryAction.onClick}
        disabled={primaryAction.disabled || primaryAction.loading}
        loading={primaryAction.loading}
      >
        {primaryAction.label}
      </JourneyButton>
    ) : null}
  </div>
);

export const ActionBar = JourneyActionBar;

export default JourneyActionBar;
